import { useEffect, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Bell, CalendarDays, CloudSun, LocateFixed, Move, Sparkles, Smartphone, Volume2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import { announce } from './islandBus'
import { osPermission, requestOsPermission, showOsNotification, type OsPermission } from './osNotifications'
import { playIslandSound, setIslandSoundsEnabled } from './sounds'
import { useIslandSettings, type IslandSettings } from './useIslandSettings'

/**
 * Dynamic Island settings, saved to the person's account, plus the two browser permissions it
 * uses (notifications and location) with their live status and a way to ask again.
 *
 * Browsers only let a site ask while the permission is still "not decided". Once someone blocks
 * it, only they can undo that from the site settings (the lock icon in the address bar), so the
 * dialog says exactly that instead of a button that silently does nothing.
 */

type LocationPermission = 'granted' | 'denied' | 'prompt' | 'unsupported'

async function locationPermission(): Promise<LocationPermission> {
  if (typeof navigator === 'undefined' || !('geolocation' in navigator)) return 'unsupported'
  try {
    const status = await navigator.permissions?.query({ name: 'geolocation' as PermissionName })
    return (status?.state as LocationPermission | undefined) ?? 'prompt'
  } catch {
    return 'prompt'
  }
}

function requestLocation(): Promise<LocationPermission> {
  return new Promise((resolve) => {
    if (!('geolocation' in navigator)) return resolve('unsupported')
    navigator.geolocation.getCurrentPosition(
      () => resolve('granted'),
      (err) => resolve(err.code === err.PERMISSION_DENIED ? 'denied' : 'prompt'),
      { timeout: 15_000, maximumAge: 0 },
    )
  })
}

export function IslandSettingsDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { settings, save } = useIslandSettings()
  const queryClient = useQueryClient()
  const [notif, setNotif] = useState<OsPermission>(osPermission())
  const [loc, setLoc] = useState<LocationPermission>('prompt')

  useEffect(() => {
    void locationPermission().then(setLoc)
    // Reflect changes the person makes in the browser's own site settings while this is open.
    let status: PermissionStatus | undefined
    void navigator.permissions?.query({ name: 'geolocation' as PermissionName }).then((s) => {
      status = s
      s.onchange = () => setLoc(s.state as LocationPermission)
    }).catch(() => undefined)
    return () => {
      if (status) status.onchange = null
    }
  }, [])

  const set = async (patch: Partial<IslandSettings>) => {
    try {
      await save(patch)
    } catch {
      toast.error('Could not save. Try again.')
    }
  }

  const askNotifications = async () => {
    const result = await requestOsPermission()
    setNotif(result)
    if (result === 'granted') {
      await set({ osNotifications: true })
      void showOsNotification({ title: 'TapWork notifications are on', body: 'New notifications and reminders will appear here.', tag: 'tapwork-test' })
    }
  }

  const askLocation = async () => {
    try {
      window.localStorage.removeItem('tapwork:island:coords:v1')
    } catch {
      // Storage optional.
    }
    const result = await requestLocation()
    setLoc(result)
    if (result === 'granted') {
      await set({ weather: true })
      void queryClient.invalidateQueries({ queryKey: ['island-weather'] })
      toast.success('Location allowed. Weather will appear in the island.')
    }
  }

  const toggleOs = async (on: boolean) => {
    if (!on) return set({ osNotifications: false })
    if (notif !== 'granted') return askNotifications()
    await set({ osNotifications: true })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Dynamic Island</DialogTitle>
          <DialogDescription>A small floating pill with the time, weather, what’s due and what’s new. Long-press it to drag it anywhere.</DialogDescription>
        </DialogHeader>

        <div className="grid gap-1">
          <Setting icon={<Smartphone className="size-4" />} title="Show the Dynamic Island" detail="Turn it off any time; nothing else changes." checked={settings.enabled} onChange={(v) => void set({ enabled: v })} />
          <Setting icon={<CalendarDays className="size-4" />} title="Morning, afternoon & evening briefs" detail="A short summary once in each part of the day." checked={settings.briefs} disabled={!settings.enabled} onChange={(v) => void set({ briefs: v })} />
          <Setting
            icon={<Volume2 className="size-4" />}
            title="Sounds"
            detail="Tiny, soft sounds when it opens, alerts and moves."
            checked={settings.sounds}
            disabled={!settings.enabled}
            onChange={(v) => {
              setIslandSoundsEnabled(v)
              if (v) playIslandSound('success')
              void set({ sounds: v })
            }}
          />
          <Setting icon={<CloudSun className="size-4" />} title="Weather" detail="Needs location, used only to ask for the weather." checked={settings.weather} disabled={!settings.enabled} onChange={(v) => void set({ weather: v })} />
          <Setting icon={<Bell className="size-4" />} title="Desktop notifications" detail="New notifications and due-soon reminders, in your OS notification center." checked={settings.osNotifications && notif === 'granted'} disabled={notif === 'unsupported' || notif === 'denied'} onChange={(v) => void toggleOs(v)} />
          <Setting icon={<Sparkles className="size-4" />} title="AI insights" detail="A short, practical line about your work, a few times a day." checked={settings.aiFacts} disabled={!settings.enabled} onChange={(v) => void set({ aiFacts: v })} />
        </div>

        <div className="rounded-xl border border-border p-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-secondary">Permissions</p>
          <PermissionRow
            icon={<Bell className="size-4" />}
            title="Notifications"
            state={notif === 'default' ? 'prompt' : notif}
            onRequest={() => void askNotifications()}
          />
          <PermissionRow icon={<LocateFixed className="size-4" />} title="Location" state={loc} onRequest={() => void askLocation()} />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border p-3">
          <span className="flex items-center gap-2 text-sm">
            <Move className="size-4 text-brand" />
            Position: <span className="font-medium">{settings.position ? 'Where you put it' : 'Automatic, per page'}</span>
          </span>
          <Button size="sm" variant="outline" disabled={!settings.position} onClick={() => { playIslandSound('drop'); void set({ position: null }) }}>Reset</Button>
        </div>

        <button
          type="button"
          className="self-start text-xs font-medium text-brand hover:underline disabled:opacity-40"
          disabled={!settings.enabled}
          onClick={() => announce({ id: `test:${Date.now()}`, title: 'This is how alerts look', body: 'New notifications appear here, then settle back.', tone: 'success' })}
        >
          Preview an alert
        </button>
      </DialogContent>
    </Dialog>
  )
}

function PermissionRow({ icon, title, state, onRequest }: { icon: ReactNode; title: string; state: 'granted' | 'denied' | 'prompt' | 'unsupported'; onRequest: () => void }) {
  const label = state === 'granted' ? 'Allowed' : state === 'denied' ? 'Blocked' : state === 'unsupported' ? 'Not supported' : 'Not asked yet'
  return (
    <div className="flex items-center gap-3 py-1.5">
      <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-surface-sunken text-text-secondary">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium text-foreground">{title}</span>
        <span className={cn('block text-xs', state === 'granted' ? 'text-success' : state === 'denied' ? 'text-danger' : 'text-text-secondary')}>
          {label}
          {state === 'denied' ? ' · allow it from the lock icon in the address bar, then reload' : ''}
        </span>
      </span>
      {state === 'prompt' || state === 'granted' ? (
        <Button size="sm" variant={state === 'granted' ? 'ghost' : 'default'} onClick={onRequest}>
          {state === 'granted' ? 'Check again' : 'Allow'}
        </Button>
      ) : null}
    </div>
  )
}

function Setting({ icon, title, detail, checked, disabled, onChange }: { icon: ReactNode; title: string; detail: string; checked: boolean; disabled?: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex cursor-pointer items-center gap-3 rounded-xl px-2 py-2.5 transition-colors hover:bg-surface-sunken has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50">
      <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-brand-soft text-brand">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium text-foreground">{title}</span>
        <span className="block text-xs text-text-secondary">{detail}</span>
      </span>
      <Switch checked={checked} disabled={disabled} onCheckedChange={onChange} />
    </label>
  )
}
